import Head from 'next/head';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { AccountFormTemplate } from '@/components/auth/AccountFormTemplate';
import ForgotPasswordForm from '@/components/auth/ForgotPasswordForm';
import { LoginForm } from '@/components/auth/LoginForm';
import { SignUpForm } from '@/components/auth/SignUpForm';
import HeadMeta from '@/components/static-pages/HeadMeta';
import Loading from '@/components/switchback/Loading/Loading';
import { PAGE_TITLE_SUFFIX } from '@/constants/pages';
import { LOGIN_PAGE } from '@/constants/urls';
import useQueryParams from '@/hooks/useQueryParams';
import { useRouter } from '@/hooks/useRouter';
import { loginUser, signupUser } from '@/redux/modules/auth';
import {
  selectAuth,
  selectAuthenticated,
  selectAuthenticating,
  selectHasTriedFetch,
} from '@/redux/modules/auth/selectors';
import { ILoginForm } from '@/services/types/auth/ILoginForm';
import { ISignupForm } from '@/services/types/auth/ISignupForm';
import { getParamAsString } from '@/utility/queryParams';
import { getReCaptchaToken } from '@/utility/recaptcha';
import { getNextRedirectURL, isPreviousPageOutdoorsy } from '@/utility/session';
import { clearSSOAuthOriginURL, getSSOAuthOriginURL } from '@/utility/sso';

interface IAuthMetaProps {
  metaTitle: string;
  metaDescription: string;
}

const ForgotPassword: FC = () => {
  return (
    <>
      <Head>
        <title>{`Forgot Password${PAGE_TITLE_SUFFIX}`}</title>
      </Head>
      <section className="flex flex-col items-center justify-center mt-16">
        <div className="w-full max-w-sm px-5 lg:px-8">
          <ForgotPasswordForm loginRedirect={LOGIN_PAGE} />
        </div>
      </section>
    </>
  );
};

const Login: FC<IAuthMetaProps> = ({ metaTitle, metaDescription }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const query = useQueryParams();

  const [hasSeenAuthError, setHasSeenAuthError] = useState(false);

  // Track if we've seen an auth error to know when to stop showing query error
  useEffect(() => {
    if (auth.error) setHasSeenAuthError(true);
  }, [auth.error]);

  // Combine the auth error with the query error.
  // Once auth error was shown, we stop showing query error.
  const loginError = auth.error || (hasSeenAuthError ? null : getParamAsString(query?.error));

  const labelHeader = intl.formatMessage({ defaultMessage: 'Welcome back!', id: '0MXX5B' });
  const labelSubHeader = intl.formatMessage({
    defaultMessage: 'It’s about time for another road trip.',
    id: 'x8+VJY',
  });

  const onLogin = useCallback((data: ILoginForm) => dispatch(loginUser(data)), [dispatch]);

  return (
    <>
      <HeadMeta title={metaTitle} description={metaDescription} hideOutdoorsyTitle />
      <div className="flex flex-col items-center justify-center my-16">
        <AccountFormTemplate className="max-w-sm px-5 lg:px-8" isPage>
          <div>
            <h2 className="mb-2 h2">{labelHeader}</h2>
            <p className="mb-12 text-gray-800 body-lg">{labelSubHeader}</p>
            <LoginForm
              onLogin={onLogin}
              isLoading={auth.isLoggingIn}
              error={loginError}
              reCaptchaToken={getReCaptchaToken()}
            />
          </div>
        </AccountFormTemplate>
      </div>
    </>
  );
};

const Signup: FC<IAuthMetaProps> = ({ metaTitle, metaDescription }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const auth = useSelector(selectAuth);
  const onSignup = useCallback((data: ISignupForm) => dispatch(signupUser(data)), [dispatch]);
  const messageSignUpHeader = intl.formatMessage(
    { defaultMessage: 'Join {company_name}', id: 'Qp0+MY' },
    { company_name: 'Outdoorsy' },
  );
  const [didAcceptTerms, setDidAcceptTerms] = useState(false);

  return (
    <>
      <HeadMeta title={metaTitle} description={metaDescription} hideOutdoorsyTitle />
      <div className="flex flex-col items-center justify-center my-16">
        <AccountFormTemplate
          className="max-w-sm px-5 lg:px-8"
          isPage
          signup
          didAcceptTerms={didAcceptTerms}>
          <h2 className="mb-12 h2">{messageSignUpHeader}</h2>
          <SignUpForm
            onSignup={onSignup}
            error={auth.error}
            reCaptchaToken={getReCaptchaToken()}
            onDidAcceptTerms={(termsState: boolean) => setDidAcceptTerms(termsState)}
            signupVerification={auth.signupVerification}
          />
        </AccountFormTemplate>
      </div>
    </>
  );
};

const AccountForm: FC = () => {
  const intl = useIntl();
  const router = useRouter();
  const query = useQueryParams();
  const isAuthenticated = useSelector(selectAuthenticated);
  const isAuthenticating = useSelector(selectAuthenticating);
  const hasTriedFetch = useSelector(selectHasTriedFetch);
  const redirectURL = useMemo(() => getNextRedirectURL(query?.next), [query]);

  const authMeta: IAuthMetaProps = {
    metaTitle: intl.formatMessage({ defaultMessage: 'Log in / Sign up', id: '78Doyj' }),
    metaDescription: intl.formatMessage({
      defaultMessage:
        'Log into your Outdoorsy account, or sign up to join the #1 most trusted RV rental marketplace in the world.',
      id: 'ZjiwW1',
    }),
  };

  useEffect(() => {
    if (!hasTriedFetch) return;
    if (isAuthenticated) {
      if (redirectURL) {
        router.replace(redirectURL);
      } else if (window?.history.length > 1 && isPreviousPageOutdoorsy()) {
        router.back();
      } else {
        const ssoAuthOriginURL = getSSOAuthOriginURL();
        router.replace(ssoAuthOriginURL || '/trips');
      }

      clearSSOAuthOriginURL();
    }
  }, [isAuthenticated, hasTriedFetch, router, redirectURL]);

  const renderAuthForm = () => {
    switch (query?.method) {
      case 'recover':
        return <ForgotPassword />;
      case 'signup':
      case 'register':
        return <Signup {...authMeta} />;
      default:
        return <Login {...authMeta} />;
    }
  };

  const isLoading = Boolean(isAuthenticated || isAuthenticating || !hasTriedFetch);

  return (
    <>
      {isLoading && (
        <div className="absolute inset-0 z-50 flex justify-center bg-white">
          <Loading className="mt-[40vh]" />
        </div>
      )}
      {renderAuthForm()}
    </>
  );
};

export default AccountForm;
